'use client';

import clsx from 'clsx';
import Image from 'next/image';

import { PAYMENT_METHODS } from '@/features/payment/const';
import { normalizeDisplayStyle } from '@/features/subscriptions/utils';
import { Tag } from '@/shared';
import { SubscriptionResponseV1 } from '@/shared/api';
import { priceFormat } from '@/shared/utils';

import { Button } from '../../../ui';

const getBiggestPriceFromOneTimePaymentInfo = (
	oneTimePaymentInfo: SubscriptionResponseV1['oneTimePaymentInfo'],
) => {
	return (
		oneTimePaymentInfo.meta?.monthlyDiscountPrice ||
		oneTimePaymentInfo.discountPrice
	);
};

const getSmallerPriceFromOneTimePaymentInfo = (
	oneTimePaymentInfo: SubscriptionResponseV1['oneTimePaymentInfo'],
) => {
	return oneTimePaymentInfo.meta?.monthlyPrice || oneTimePaymentInfo.price;
};

/** Зачёркнутая цена у «1 ₽» по промокоду: не показываем 0, если скидка только в meta, а discountPrice не заполнен */
const getPromoStrikethroughPrice = (
	oneTime: SubscriptionResponseV1['oneTimePaymentInfo'],
	hasDiscount: boolean,
): number => {
	if (hasDiscount && oneTime.discountPrice > 0) {
		return oneTime.discountPrice;
	}
	if (oneTime.price > 0) {
		return oneTime.price;
	}
	const perMonth = getSmallerPriceFromOneTimePaymentInfo(oneTime);
	if (perMonth > 0) {
		return perMonth;
	}
	const withMetaDiscount = getBiggestPriceFromOneTimePaymentInfo(oneTime);
	return withMetaDiscount && withMetaDiscount > 0 ? withMetaDiscount : perMonth;
};

interface SubscriptionsItemProps {
	data: SubscriptionResponseV1;
	promocode?: string;
	isPromoCodeValid?: boolean;
}

export const SubscriptionsItem = ({
	data,
	promocode,
	isPromoCodeValid,
}: SubscriptionsItemProps) => {
	const displayStyle = normalizeDisplayStyle(
		data.subscriptionInfo?.displayStyle,
	);
	const isPromoAllowed = data.isPromoAllowed ?? false;

	const showDiscount = Boolean(
		(data.oneTimePaymentInfo.meta?.monthlyDiscountPrice &&
			data.oneTimePaymentInfo.meta.monthlyDiscountPrice > 0) ||
			(data.oneTimePaymentInfo.discountPrice &&
				data.oneTimePaymentInfo.discountPrice > 0),
	);

	const getPaymentLink = ({
		subscriptionId,
		paymentMethod,
	}: {
		subscriptionId: string;
		paymentMethod: (typeof PAYMENT_METHODS)[keyof typeof PAYMENT_METHODS];
	}) => {
		const baseUrl = `/payment/subscription/${subscriptionId}?paymentMethod=${paymentMethod}`;
		// Добавляем промокод в URL, если он валидный
		if (isPromoCodeValid && promocode) {
			return `${baseUrl}&promocode=${promocode}`;
		}
		return baseUrl;
	};

	return (
		<div
			className={clsx(
				'relative flex h-full flex-col overflow-hidden rounded-[40px] p-6',
				{
					'border-0 bg-[#FAEDED] text-black shadow-[0_4px_20px_rgba(220,38,38,0.2)]':
						displayStyle === 'new-year-promo',
					'border-0 bg-white text-gray-900 shadow-md':
						displayStyle === 'default',
					'border-0 bg-gray-50 text-gray-900 shadow-md':
						displayStyle === 'white',
					'border-0 bg-gray-900 text-white shadow-md': displayStyle === 'black',
				},
			)}
		>
			{displayStyle === 'new-year-promo' && (
				<div className="absolute right-4 top-4 text-3xl opacity-80">🎄</div>
			)}
			{displayStyle === 'new-year-promo' && (
				<>
					<div className="absolute left-6 top-12 text-2xl opacity-40">❄️</div>
					<div className="absolute right-8 top-1/3 text-xl opacity-35">❄️</div>
					<div className="absolute bottom-1/4 left-8 text-2xl opacity-40">
						❄️
					</div>
					<div className="absolute bottom-16 right-6 text-xl opacity-35">
						❄️
					</div>
				</>
			)}
			<div
				className={clsx(
					'mx-auto w-52 max-w-full',
					displayStyle === 'black' &&
						'text-white [&_img]:brightness-0 [&_img]:invert',
				)}
			>
				<Image
					src="/landings/algorithms/grade-up.svg"
					alt="Как вступить в сообщество?"
					width={875}
					height={505}
					className="h-auto w-full"
				/>
			</div>

			<div className="pb-4">
				<h3 className="text-2xl font-bold">
					{data.subscriptionInfo?.name ?? 'Не указано'}
				</h3>
			</div>

			{/* Features */}
			<div className="mb-6 space-y-3">
				{data.subscriptionInfo.features?.map((feature, index) => (
					<div key={index} className="flex items-start gap-2">
						<div
							className={clsx('mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full', {
								'bg-[#DC2626]': displayStyle === 'new-year-promo',
								'bg-white': displayStyle === 'black',
								'bg-gray-500': displayStyle === 'white',
								'bg-gray-600': displayStyle === 'default',
							})}
						/>
						<span
							className="text-sm leading-relaxed"
							dangerouslySetInnerHTML={{ __html: feature }}
						/>
					</div>
				))}
			</div>

			<div className="mt-auto">
				{/* Price */}
				<div className="mb-6 space-y-1">
					<div className="flex flex-wrap items-center gap-3">
						{isPromoCodeValid && isPromoAllowed ? (
							// Отображение при валидном промокоде
							<>
								<span className="text-3xl font-bold">1 ₽</span>
								<div className="flex flex-wrap items-center gap-3">
									<span className="text-lg line-through opacity-60">
										{priceFormat({
											value: getPromoStrikethroughPrice(
												data.oneTimePaymentInfo,
												showDiscount,
											),
										})}
									</span>
									<Tag type="info">за первые 3 дня</Tag>
								</div>
							</>
						) : showDiscount ? (
							<>
								<span className="text-3xl font-bold">
									{priceFormat({
										value: getBiggestPriceFromOneTimePaymentInfo(
											data.oneTimePaymentInfo,
										),
										withCurrency: false,
									})}{' '}
									₽/мес
								</span>
								<div className="flex flex-wrap items-center gap-3">
									<span className="text-lg line-through opacity-60">
										{priceFormat({
											value: getSmallerPriceFromOneTimePaymentInfo(
												data.oneTimePaymentInfo,
											),
											withCurrency: false,
										})}{' '}
										₽/мес
									</span>
									{/* <span className="rounded bg-green-500 px-2 py-1 text-xs text-white">
										{discountPercentage.toFixed(0)}%
									</span> */}
								</div>
							</>
						) : (
							<span className="text-3xl font-bold">
								{priceFormat({
									value: getSmallerPriceFromOneTimePaymentInfo(
										data.oneTimePaymentInfo,
									),
									withCurrency: false,
								})}{' '}
								₽/мес
							</span>
						)}
					</div>
					{data.loanPaymentInfo.startingFromPerMonth > 0 && (
						<div className="flex flex-col gap-1">
							<p className="text-sm opacity-80">
								{data.loanPaymentInfo.enabled
									? `от ${priceFormat({
											value: data.loanPaymentInfo.startingFromPerMonth,
											withCurrency: false,
										})} ₽/мес`
									: ''}
							</p>
						</div>
					)}
					{data.oneTimePaymentInfo.meta?.monthlyDiscountPrice && (
						<p className="text-sm text-gray-500">при оплате полного пакета</p>
					)}
				</div>
				{/* Buttons */}
				<div className="mb-6 space-y-3">
					{data.oneTimePaymentInfo.enabled && (
						<Button
							href={getPaymentLink({
								subscriptionId: data.subscriptionId,
								paymentMethod: PAYMENT_METHODS.FULL,
							})}
							variant="primary"
							className={clsx(
								'w-full',
								displayStyle === 'new-year-promo' &&
									'!bg-[#DC2626] !text-white hover:!bg-[#B91C1C] focus-visible:!outline-[#DC2626] active:!bg-[#991B1B]',
							)}
							size="48"
						>
							Оплатить полностью
						</Button>
					)}
					{data.loanPaymentInfo.enabled && (
						<Button
							href={getPaymentLink({
								subscriptionId: data.subscriptionId,
								paymentMethod: PAYMENT_METHODS.LOAN,
							})}
							variant="ghost"
							className={clsx(
								'w-full',
								displayStyle === 'new-year-promo' &&
									'border-[#DC2626] text-[#DC2626] hover:bg-[#FAEDED]',
							)}
							size="48"
						>
							Оформить рассрочку
						</Button>
					)}

					{data.dolyamiPaymentInfo.enabled && (
						<Button
							variant="ghost"
							href={getPaymentLink({
								subscriptionId: data.subscriptionId,
								paymentMethod: PAYMENT_METHODS.DOLYAMI,
							})}
							size="48"
							className={clsx(
								'w-full',
								displayStyle === 'new-year-promo' &&
									'border-[#DC2626] text-[#DC2626] hover:bg-[#FAEDED]',
							)}
						>
							Оплатить долями
						</Button>
					)}
				</div>
			</div>
		</div>
	);
};
